import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LabelElement, LabelConfig } from '@/types'
import { generateId } from '@/lib/utils/generateId'

export interface SavedDesign {
  id: string
  name: string
  elements: LabelElement[]
  labelConfig: LabelConfig
  createdAt: number
  updatedAt: number
}

export interface ExportedDesign {
  version: string
  exportedAt: string
  design: SavedDesign
}

export interface ExportedDesignsCollection {
  version: string
  exportedAt: string
  designs: SavedDesign[]
}

interface SavedDesignsState {
  designs: SavedDesign[]
  currentDesignId: string | null

  saveDesign: (name: string, elements: LabelElement[], labelConfig: LabelConfig) => string
  updateDesign: (id: string, elements: LabelElement[], labelConfig: LabelConfig) => void
  renameDesign: (id: string, name: string) => void
  deleteDesign: (id: string) => void
  loadDesign: (id: string) => SavedDesign | undefined
  setCurrentDesignId: (id: string | null) => void
  getDesignById: (id: string) => SavedDesign | undefined
  exportDesign: (id: string) => ExportedDesign | null
  exportAllDesigns: () => ExportedDesignsCollection
  importDesign: (data: ExportedDesign | ExportedDesignsCollection) => { success: boolean; count: number; error?: string }
}

export const useSavedDesignsStore = create<SavedDesignsState>()(
  persist(
    (set, get) => ({
      designs: [],
      currentDesignId: null,

      saveDesign: (name, elements, labelConfig) => {
        const id = generateId()
        const now = Date.now()
        const newDesign: SavedDesign = {
          id,
          name,
          elements,
          labelConfig,
          createdAt: now,
          updatedAt: now,
        }
        set((state) => ({
          designs: [...state.designs, newDesign],
          currentDesignId: id,
        }))
        return id
      },

      updateDesign: (id, elements, labelConfig) => {
        set((state) => ({
          designs: state.designs.map((d) =>
            d.id === id
              ? { ...d, elements, labelConfig, updatedAt: Date.now() }
              : d
          ),
        }))
      },

      renameDesign: (id, name) => {
        set((state) => ({
          designs: state.designs.map((d) =>
            d.id === id ? { ...d, name, updatedAt: Date.now() } : d
          ),
        }))
      },

      deleteDesign: (id) => {
        set((state) => ({
          designs: state.designs.filter((d) => d.id !== id),
          currentDesignId: state.currentDesignId === id ? null : state.currentDesignId,
        }))
      },

      loadDesign: (id) => {
        const design = get().designs.find((d) => d.id === id)
        if (design) {
          set({ currentDesignId: id })
        }
        return design
      },

      setCurrentDesignId: (id) => set({ currentDesignId: id }),

      getDesignById: (id) => get().designs.find((d) => d.id === id),

      exportDesign: (id) => {
        const design = get().designs.find((d) => d.id === id)
        if (!design) return null
        return {
          version: '1.0',
          exportedAt: new Date().toISOString(),
          design,
        }
      },

      exportAllDesigns: () => {
        return {
          version: '1.0',
          exportedAt: new Date().toISOString(),
          designs: get().designs,
        }
      },

      importDesign: (data) => {
        try {
          const designs: SavedDesign[] = []

          // Check if it's a single design or a collection
          if ('design' in data && data.design) {
            designs.push(data.design)
          } else if ('designs' in data && Array.isArray(data.designs)) {
            designs.push(...data.designs)
          } else {
            return { success: false, count: 0, error: 'Invalid file format' }
          }

          if (designs.length === 0) {
            return { success: false, count: 0, error: 'No designs found in file' }
          }

          // Import designs with new IDs to avoid conflicts
          const existingIds = new Set(get().designs.map((d) => d.id))
          const now = Date.now()
          const newDesigns = designs.map((d) => ({
            ...d,
            id: existingIds.has(d.id) ? generateId() : d.id,
            updatedAt: now,
          }))

          set((state) => ({
            designs: [...state.designs, ...newDesigns],
          }))

          return { success: true, count: newDesigns.length }
        } catch {
          return { success: false, count: 0, error: 'Failed to parse file' }
        }
      },
    }),
    {
      name: 'zpl-saved-designs',
    }
  )
)
